import { ChevronUpDownIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { Checkbox } from '@/components/form/Checkbox';
import { Date, DateProps } from '@/components/form/Date';
import { FormGroup, FormInfo } from '@/components/form/FormGroup';
import { InputText } from '@/components/form/InputText';
import { Select, SelectProps } from '@/components/form/Select';

// eslint-disable-next-line react-refresh/only-export-components
export enum Type {
  PASSWORD = 'PASSWORD',
  TEXT_AREA = 'TEXT_AREA',
  SELECT = 'SELECT',
  DATE = 'DATE',
  CHECKBOX = 'CHECKBOX',
}

export type InputEvent = React.ChangeEvent<
  HTMLInputElement | HTMLTextAreaElement
>;

export interface InputProps extends SelectProps, DateProps {
  className?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  label?: React.ReactElement<any> | string;
  value: string | number;
  onChange: (e: InputEvent) => void;
  type?: Type;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  name: string;
  id?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  required?: boolean;
  hidden?: boolean;
  info?: FormInfo;
  password?: boolean;
  onFocus?: (event: React.FocusEvent) => void;
  onBlur?: (event: React.FocusEvent) => void;
  pattern?: string;
  isLoading?: boolean;
  onClick?: (event: React.MouseEvent) => void;
  showTick?: boolean;
}

export function Input(props: InputProps) {
  const {
    startIcon,
    className: classNameProps,
    info,
    isLoading,
    endIcon,
    ...rest
  } = props;
  const className = `${classNameProps} ${startIcon ? 'pl-10!' : ''}`;

  // Allow fields and labels to be hidden
  if (props.hidden) return null;

  switch (props.type) {
    case Type.SELECT:
      return (
        <FormGroup
          {...rest}
          label={props.label}
          error={props.error}
          name={props.name}
          startIcon={startIcon}
          isLoading={isLoading}
          info={info}
          endIcon={
            endIcon ?? (
              <ChevronUpDownIcon
                className="text-chariot-text-secondary size-5"
                aria-hidden="true"
              />
            )
          }
        >
          <Select {...rest} isLoading={isLoading} className={className} />
        </FormGroup>
      );

    case Type.CHECKBOX:
      return (
        <FormGroup
          {...rest}
          className="shadow-none"
          label={''}
          error={props.error}
          name={props.name}
          startIcon={startIcon}
          isLoading={isLoading}
          info={info}
        >
          <Checkbox {...rest} info={info} className={className} />
        </FormGroup>
      );

    case Type.DATE:
      return (
        <FormGroup
          label={props.label}
          error={props.error}
          name={props.name}
          startIcon={startIcon}
          isLoading={isLoading}
          info={info}
          endIcon={endIcon}
        >
          <Date {...rest} className={className} />
        </FormGroup>
      );

    default:
      return (
        <FormGroup
          label={props.label}
          error={props.error}
          name={props.name}
          startIcon={startIcon}
          isLoading={isLoading}
          info={info}
          endIcon={endIcon}
        >
          <InputText {...rest} className={className} />
        </FormGroup>
      );
  }
}

Input.Type = Type;
